// src/components/AssistantPanel.js — the "✨ Ask" assistant (bottom-right on every staff screen)
// [marker: ai-assistant v1]
// The conversation lives only in this browser tab — nothing is saved; reloading the page clears it.
// Actions come back as cards; nothing happens until the person presses Confirm.
import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

const TOOL_LABELS = {
  search_members: 'members', member_details: 'member details', membership_overview: 'membership numbers', renewals_due: 'renewals',
  search_contacts: 'contacts', list_events: 'events', event_details: 'event details', event_attendees: 'attendees',
  profit_and_loss: 'profit & loss', open_invoices: 'unpaid invoices', search_invoices: 'invoices', payments_received: 'payments received',
  account_balances: 'account balances', unpaid_bills: 'unpaid bills', expenses_by_vendor: 'expenses by vendor',
  propose_create_task: 'task', propose_add_contact_note: 'note', propose_check_in: 'check-in',
};
const SUGGEST = {
  full: ['Which invoices are more than 30 days overdue?', 'How much money came in last month?', 'What’s our profit so far this year?', 'Who renews in the next 60 days?'],
  limited: ['Who renews in the next 60 days?', 'How many tickets have we sold for the next event?', 'Find the contact info for …', 'How many active members do we have?'],
};

// tiny, safe formatter: **bold**, bullet lines, simple | tables |
function Rich({ text }) {
  const lines = String(text || '').split('\n');
  const out = []; let i = 0;
  const inline = (s, k) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => (p.startsWith('**') && p.endsWith('**') ? <strong key={`${k}-${j}`}>{p.slice(2, -2)}</strong> : <React.Fragment key={`${k}-${j}`}>{p}</React.Fragment>));
  while (i < lines.length) {
    const l = lines[i];
    if (/^\s*\|.*\|\s*$/.test(l)) {
      const rows = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { if (!/^\s*\|[\s:|-]+\|\s*$/.test(lines[i])) rows.push(lines[i].trim().slice(1, -1).split('|').map(c => c.trim())); i++; }
      out.push(
        <div key={`t${i}`} style={{ overflowX: 'auto', margin: '6px 0' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => (ri === 0
              ? <th key={ci} style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid var(--gray-300)', whiteSpace: 'nowrap' }}>{inline(c, `h${ri}${ci}`)}</th>
              : <td key={ci} style={{ padding: '3px 6px', borderBottom: '1px solid var(--gray-100)', whiteSpace: 'nowrap' }}>{inline(c, `c${ri}${ci}`)}</td>))}</tr>)}</tbody>
          </table>
        </div>);
      continue;
    }
    if (/^\s*([-•*]|\d+\.)\s+/.test(l)) out.push(<div key={i} style={{ paddingLeft: 14, textIndent: -10 }}>• {inline(l.replace(/^\s*([-•*]|\d+\.)\s+/, ''), i)}</div>);
    else if (/^#+\s/.test(l)) out.push(<div key={i} style={{ fontWeight: 700, marginTop: 6 }}>{inline(l.replace(/^#+\s/, ''), i)}</div>);
    else if (!l.trim()) out.push(<div key={i} style={{ height: 6 }} />);
    else out.push(<div key={i}>{inline(l, i)}</div>);
    i++;
  }
  return <>{out}</>;
}

export default function AssistantPanel() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);   // { role, content, proposals?, used?, error? }
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get('/assistant/status').then(r => setStatus(r.data)).catch(() => setStatus({ access: 'none' })); }, []);
  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ block: 'end' }); }, [msgs, busy, open]);
  if (!status || status.access === 'none') return null;

  const send = async (text) => {
    const q = String(text || input).trim();
    if (!q || busy) return;
    const next = [...msgs, { role: 'user', content: q }];
    setMsgs(next); setInput(''); setBusy(true);
    try {
      const history = next.filter(m => !m.error).map(m => ({ role: m.role, content: m.content }));
      const r = await api.post('/assistant/chat', { messages: history });
      setMsgs(m => [...m, { role: 'assistant', content: r.data.reply, proposals: (r.data.proposals || []).map(p => ({ ...p, state: 'ready' })), used: r.data.used || [] }]);
    } catch (err) {
      setMsgs(m => [...m, { role: 'assistant', content: err.response?.data?.error || 'Something went wrong — try again.', error: true }]);
    } finally { setBusy(false); }
  };
  const decide = async (mi, pi, ok) => {
    const setP = (patch) => setMsgs(m => m.map((x, i) => (i !== mi ? x : { ...x, proposals: x.proposals.map((p, j) => (j === pi ? { ...p, ...patch } : p)) })));
    if (!ok) return setP({ state: 'cancelled' });
    setP({ state: 'working' });
    try { const r = await api.post('/assistant/confirm', { token: msgs[mi].proposals[pi].token }); setP({ state: 'done', result: r.data.message }); }
    catch (err) { setP({ state: 'ready', result: err.response?.data?.error || 'Could not do it.' }); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} title="Ask the assistant"
                style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 1300, background: 'var(--navy, #1a3a5c)', color: 'white', border: 'none', borderRadius: 24,
                         padding: '11px 18px', fontWeight: 700, fontSize: '0.9rem', boxShadow: '0 6px 18px rgba(0,0,0,0.25)', cursor: 'pointer' }}>
          ✨ Ask
        </button>
      )}
      {open && (
        <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1300, width: 'min(440px, calc(100vw - 32px))', height: 'min(640px, calc(100vh - 32px))',
                      background: 'white', borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.28)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ background: 'var(--navy, #1a3a5c)', color: 'white', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ flex: 1 }}>✨ Assistant</strong>
            {msgs.length > 0 && <button onClick={() => setMsgs([])} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: 'white', borderRadius: 6, fontSize: '0.75rem', padding: '2px 8px', cursor: 'pointer' }}>New chat</button>}
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'transparent', border: 'none', color: 'white', fontSize: 18, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, fontSize: '0.86rem', lineHeight: 1.45, background: 'var(--gray-50, #f9fafb)' }}>
            {msgs.length === 0 && (
              <div style={{ color: 'var(--gray-600)' }}>
                <p style={{ marginTop: 0 }}>Ask about {status.access === 'full' ? 'members, events, invoices, payments or the books' : 'members, contacts and events'}. I can also set up a task, add a note to a contact or check someone in — you confirm first.</p>
                <div style={{ display: 'grid', gap: 6 }}>
                  {SUGGEST[status.access].map(s => (
                    <button key={s} onClick={() => (s.endsWith('…') ? setInput(s.replace('…', '')) : send(s))}
                            style={{ textAlign: 'left', background: 'white', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', fontSize: '0.83rem' }}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, mi) => (
              <div key={mi} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', margin: '8px 0' }}>
                <div style={{ maxWidth: '88%', borderRadius: 10, padding: '8px 11px', background: m.role === 'user' ? 'var(--navy, #1a3a5c)' : m.error ? '#fef2f2' : 'white',
                              color: m.role === 'user' ? 'white' : m.error ? '#991b1b' : 'inherit', border: m.role === 'user' ? 'none' : '1px solid var(--gray-200)' }}>
                  {m.role === 'user' ? m.content : <Rich text={m.content} />}
                  {(m.proposals || []).map((p, pi) => (
                    <div key={pi} style={{ marginTop: 8, border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 6 }}>{p.summary}</div>
                      {p.state === 'ready' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-primary btn-sm" onClick={() => decide(mi, pi, true)}>Confirm</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => decide(mi, pi, false)}>Cancel</button>
                        </div>
                      )}
                      {p.state === 'working' && <span style={{ fontSize: '0.8rem' }}>Working…</span>}
                      {p.state === 'cancelled' && <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>Cancelled — nothing was changed.</span>}
                      {p.result && <div style={{ fontSize: '0.8rem', marginTop: 4, color: p.state === 'done' ? 'var(--success)' : 'var(--danger)' }}>{p.state === 'done' ? '✓ ' : ''}{p.result}</div>}
                    </div>
                  ))}
                  {m.used && m.used.filter(u => !u.startsWith('propose_')).length > 0 && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', marginTop: 6 }}>Looked at: {Array.from(new Set(m.used.filter(u => !u.startsWith('propose_')).map(u => TOOL_LABELS[u] || u))).join(', ')}</div>
                  )}
                </div>
              </div>
            ))}
            {busy && <div style={{ color: 'var(--gray-500)', fontSize: '0.82rem', margin: '8px 0' }}>Thinking…</div>}
            <div ref={endRef} />
          </div>
          <div style={{ borderTop: '1px solid var(--gray-200)', padding: 8, display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <textarea value={input} onChange={e => setInput(e.target.value)} rows={2} placeholder="Ask a question…" disabled={busy}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                      style={{ flex: 1, resize: 'none', border: '1px solid var(--gray-300)', borderRadius: 8, padding: '7px 9px', fontSize: '0.86rem', fontFamily: 'inherit' }} />
            <button className="btn btn-primary btn-sm" onClick={() => send()} disabled={busy || !input.trim()} style={{ height: 38 }}>Send</button>
          </div>
          <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', padding: '0 10px 6px' }}>
            Answers come from the app’s data — double-check anything important. This chat isn’t saved{status.access === 'limited' ? ' · membership & events only' : ''}.
          </div>
        </div>
      )}
    </>
  );
}
